import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import {
  Search, Edit, Loader2, Boxes, Lock, Puzzle, Share2,
  LayoutDashboard, Users, Clock, Calendar, DollarSign, ListChecks,
  MessageCircle, BookOpen, Newspaper, Receipt, TrendingUp, UserPlus,
  FileText, CalendarDays, BarChart3, FileSignature, Rocket, Package,
  Filter, Building, Settings2, Target, QrCode, CreditCard, Settings,
  FileEdit
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const ICON_MAP = {
  LayoutDashboard, Users, Clock, Calendar, DollarSign, ListChecks,
  MessageCircle, BookOpen, Newspaper, Receipt, TrendingUp, UserPlus,
  FileText, CalendarDays, BarChart3, FileSignature, Rocket, Package,
  Boxes, Puzzle, Share2, Building, Lock, Filter, Search, Edit, Settings2,
  Target, QrCode, CreditCard, Settings, FileEdit
};

const MODULE_TYPES = [
  { value: 'core', label: 'Core', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Lock, desc: 'Available to all offices' },
  { value: 'custom', label: 'Custom', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Puzzle, desc: 'Built for a specific office' },
  { value: 'reusable', label: 'Re-usable', color: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: Share2, desc: 'Assignable to multiple offices' },
];

const CATEGORY_OPTIONS = ['General', 'HR', 'Finance', 'Operations', 'Communications'];

const getModuleTypeConfig = (type) => MODULE_TYPES.find(t => t.value === type) || MODULE_TYPES[0];

// Map kebab-case icon names to PascalCase for backward compatibility
const ICON_ALIAS = {
  'users': 'Users', 'clock': 'Clock', 'calendar': 'Calendar',
  'dollar-sign': 'DollarSign', 'list-checks': 'ListChecks', 'receipt': 'Receipt',
  'bar-chart': 'BarChart3', 'newspaper': 'Newspaper', 'book-open': 'BookOpen',
  'message-circle': 'MessageCircle', 'file-text': 'FileText', 'file-edit': 'FileText',
  'target': 'Target', 'mail': 'FileText', 'qr-code': 'QrCode',
  'credit-card': 'CreditCard', 'settings': 'Settings2', 'trending-up': 'TrendingUp',
  'file-signature': 'FileSignature',
};

const DynamicIcon = ({ name, className }) => {
  const resolved = ICON_ALIAS[name] || name;
  const IconComponent = ICON_MAP[resolved];
  if (!IconComponent) return <Package className={className} />;
  return <IconComponent className={className} />;
};

const ModuleManagement = () => {
  const [modules, setModules] = useState([]);
  const [offices, setOffices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedModule, setSelectedModule] = useState(null);
  const [editType, setEditType] = useState('core');
  const [editActive, setEditActive] = useState(true);
  const [editOfficeId, setEditOfficeId] = useState('');
  const [editOfficeIds, setEditOfficeIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const { getAuthHeaders } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchModules = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/modules/`, { headers: getAuthHeaders() });
      if (res.ok) {
        setModules(await res.json());
      } else {
        toast.error('Failed to load modules');
      }
    } catch {
      toast.error('Network error loading modules');
    } finally {
      setLoading(false);
    }
  }, [API_URL, getAuthHeaders]);

  const fetchOffices = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/offices/`, { headers: getAuthHeaders() });
      if (res.ok) setOffices(await res.json());
    } catch { /* silent */ }
  }, [API_URL, getAuthHeaders]);

  useEffect(() => {
    fetchModules();
    fetchOffices();
  }, [fetchModules, fetchOffices]);

  const openEdit = (mod) => {
    setSelectedModule(mod);
    setEditType(mod.module_type);
    setEditActive(mod.is_active);
    setEditOfficeId(mod.assigned_office_id || '');
    setEditOfficeIds(mod.assigned_office_ids || []);
    setShowEditDialog(true);
  };

  const handleSave = async () => {
    setSubmitting(true);
    try {
      const body = {
        module_type: editType,
        is_active: editActive,
        assigned_office_id: editType === 'custom' ? editOfficeId || null : null,
        assigned_office_ids: editType === 'reusable' ? editOfficeIds : [],
      };
      const res = await fetch(`${API_URL}/api/modules/${selectedModule.id}`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        toast.success(`${selectedModule.name} updated`);
        setShowEditDialog(false);
        setSelectedModule(null);
        fetchModules();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to update');
      }
    } catch {
      toast.error('Network error');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleOfficeId = (officeId) => {
    setEditOfficeIds(prev =>
      prev.includes(officeId) ? prev.filter(id => id !== officeId) : [...prev, officeId]
    );
  };

  // Filtering
  const filtered = modules.filter(m => {
    const matchSearch = !search || m.name.toLowerCase().includes(search.toLowerCase()) || (m.description || '').toLowerCase().includes(search.toLowerCase());
    const matchType = filterType === 'all' || m.module_type === filterType;
    const matchCategory = filterCategory === 'all' || m.category === filterCategory;
    return matchSearch && matchType && matchCategory;
  });

  const coreModules = filtered.filter(m => m.module_type === 'core');
  const customModules = filtered.filter(m => m.module_type === 'custom');
  const reusableModules = filtered.filter(m => m.module_type === 'reusable');

  const stats = {
    total: modules.length,
    core: modules.filter(m => m.module_type === 'core').length,
    custom: modules.filter(m => m.module_type === 'custom').length,
    reusable: modules.filter(m => m.module_type === 'reusable').length,
    active: modules.filter(m => m.is_active).length,
  };

  // Module card
  const ModuleCard = ({ mod }) => {
    const typeConfig = getModuleTypeConfig(mod.module_type);
    return (
      <div
        className={`group relative border rounded-xl p-4 transition-all duration-200 hover:shadow-md cursor-pointer ${
          mod.is_active ? 'bg-card hover:border-primary/30' : 'bg-muted/40 opacity-60'
        }`}
        onClick={() => openEdit(mod)}
        data-testid={`module-card-${mod.id}`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <div className={`p-2 rounded-lg ${mod.is_active ? 'bg-primary/10' : 'bg-muted'}`}>
              <DynamicIcon name={mod.icon} className={`w-5 h-5 ${mod.is_active ? 'text-primary' : 'text-muted-foreground'}`} />
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-sm truncate">{mod.name}</h3>
              {mod.description && (
                <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{mod.description}</p>
              )}
            </div>
          </div>
          <Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" data-testid={`edit-module-${mod.id}`}>
            <Settings2 className="w-3.5 h-3.5" />
          </Button>
        </div>
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${typeConfig.color}`}>
            {typeConfig.label}
          </Badge>
          {mod.category && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0">{mod.category}</Badge>
          )}
          {!mod.is_active && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-gray-100 text-gray-500">Inactive</Badge>
          )}
        </div>
        {mod.module_type === 'reusable' && (
          <div className="mt-2">
            {mod.assigned_office_names && mod.assigned_office_names.length > 0 ? (
              <div className="flex items-center gap-1 flex-wrap">
                <Building className="w-3 h-3 text-muted-foreground shrink-0" />
                {mod.assigned_office_names.map((name, i) => (
                  <span key={i} className="text-[10px] bg-accent px-1.5 py-0.5 rounded">{name}</span>
                ))}
              </div>
            ) : (
              <span className="text-[10px] text-amber-600">Not assigned to any office</span>
            )}
          </div>
        )}
        {mod.module_type === 'custom' && (
          <div className="mt-2">
            {mod.assigned_office_names && mod.assigned_office_names.length > 0 ? (
              <div className="flex items-center gap-1">
                <Building className="w-3 h-3 text-muted-foreground" />
                <span className="text-[10px] bg-accent px-1.5 py-0.5 rounded">{mod.assigned_office_names[0]}</span>
              </div>
            ) : (
              <span className="text-[10px] text-amber-600">No office assigned</span>
            )}
          </div>
        )}
      </div>
    );
  };

  const ModuleSection = ({ title, icon: SectionIcon, modules: sectionModules, typeConfig }) => {
    if (sectionModules.length === 0) return null;
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <SectionIcon className="w-4 h-4 text-muted-foreground" />
          <h2 className="text-sm font-semibold">{title}</h2>
          <Badge variant="secondary" className="text-xs">{sectionModules.length}</Badge>
          {typeConfig && <span className="text-xs text-muted-foreground">{typeConfig.desc}</span>}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {sectionModules.map(mod => <ModuleCard key={mod.id} mod={mod} />)}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="modules-loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="module-management-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Module Management</h1>
        <p className="text-sm text-muted-foreground mt-1">Configure module types and assign them to offices. Click any module to edit.</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          { label: 'Total', value: stats.total, icon: Boxes, color: 'text-foreground' },
          { label: 'Core', value: stats.core, icon: Lock, color: 'text-blue-600' },
          { label: 'Custom', value: stats.custom, icon: Puzzle, color: 'text-amber-600' },
          { label: 'Re-usable', value: stats.reusable, icon: Share2, color: 'text-emerald-600' },
          { label: 'Active', value: stats.active, icon: Package, color: 'text-primary' },
        ].map(s => (
          <Card key={s.label} className="p-3" data-testid={`stat-${s.label.toLowerCase()}`}>
            <div className="flex items-center gap-3">
              <s.icon className={`w-5 h-5 ${s.color}`} />
              <div>
                <p className="text-xl font-bold">{s.value}</p>
                <p className="text-xs text-muted-foreground">{s.label}</p>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            data-testid="module-search-input"
            placeholder="Search modules..."
            className="pl-9"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-[140px]" data-testid="filter-type-select">
            <Filter className="w-3.5 h-3.5 mr-1" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {MODULE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-[150px]" data-testid="filter-category-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {CATEGORY_OPTIONS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Module Sections */}
      <div className="space-y-6">
        <ModuleSection title="Core Modules" icon={Lock} modules={coreModules} typeConfig={getModuleTypeConfig('core')} />
        <ModuleSection title="Re-usable Modules" icon={Share2} modules={reusableModules} typeConfig={getModuleTypeConfig('reusable')} />
        <ModuleSection title="Custom Modules" icon={Puzzle} modules={customModules} typeConfig={getModuleTypeConfig('custom')} />

        {filtered.length === 0 && (
          <div className="text-center py-12 text-muted-foreground" data-testid="no-modules-found">
            <Boxes className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="font-medium">No modules found</p>
            <p className="text-sm mt-1">Try adjusting your search or filters</p>
          </div>
        )}
      </div>

      {/* Edit/Configure Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md" data-testid="edit-module-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedModule && <DynamicIcon name={selectedModule.icon} className="w-5 h-5 text-primary" />}
              {selectedModule?.name}
            </DialogTitle>
            <DialogDescription>{selectedModule?.description || 'Configure this module'}</DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-2">
            {/* Module Type */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Module Type</Label>
              <div className="grid grid-cols-3 gap-2">
                {MODULE_TYPES.map(t => (
                  <button
                    key={t.value}
                    onClick={() => setEditType(t.value)}
                    className={`flex flex-col items-center gap-1.5 p-3 rounded-lg border-2 transition-all text-center ${
                      editType === t.value
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/30'
                    }`}
                    data-testid={`type-option-${t.value}`}
                  >
                    <t.icon className={`w-5 h-5 ${editType === t.value ? 'text-primary' : 'text-muted-foreground'}`} />
                    <span className="text-xs font-medium">{t.label}</span>
                  </button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">{getModuleTypeConfig(editType).desc}</p>
            </div>

            {/* Custom → single office */}
            {editType === 'custom' && (
              <div className="space-y-2">
                <Label>Assigned Office</Label>
                <Select value={editOfficeId || ''} onValueChange={setEditOfficeId}>
                  <SelectTrigger data-testid="edit-office-select">
                    <SelectValue placeholder="Select an office" />
                  </SelectTrigger>
                  <SelectContent>
                    {offices.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Reusable → multi-office */}
            {editType === 'reusable' && (
              <div className="space-y-2">
                <Label>Assigned Offices <span className="text-muted-foreground font-normal">({editOfficeIds.length} selected)</span></Label>
                <div className="border rounded-lg p-3 max-h-40 overflow-y-auto space-y-1" data-testid="edit-offices-list">
                  {offices.length === 0 && <p className="text-sm text-muted-foreground">No offices available</p>}
                  {offices.map(o => (
                    <label key={o.id} className="flex items-center gap-2 cursor-pointer hover:bg-accent rounded px-2 py-1.5 transition-colors">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300"
                        checked={editOfficeIds.includes(o.id)}
                        onChange={() => toggleOfficeId(o.id)}
                      />
                      <Building className="w-3.5 h-3.5 text-muted-foreground" />
                      <span className="text-sm">{o.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Active toggle */}
            <div className="flex items-center justify-between border rounded-lg p-3">
              <div>
                <p className="text-sm font-medium">Active</p>
                <p className="text-xs text-muted-foreground">
                  {editActive ? 'Module is visible to assigned offices' : 'Module is hidden from all offices'}
                </p>
              </div>
              <Switch
                data-testid="edit-active-toggle"
                checked={editActive}
                onCheckedChange={setEditActive}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={submitting} data-testid="save-module-btn">
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ModuleManagement;
